import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { useTheme } from '../theme/ThemeContext'
import { outcomeAvailable } from './battedBall'
import { OUT_TYPES, PRIMARY_OUTCOMES } from './outcomeChoices'
import type { OutcomePickerProps } from './outcomePickerProps'
import type { Outcome } from './scoringContract'
import {
  CONTROL_COLORS,
  CONTROL_HEIGHT,
  ControlBackdrop,
  ControlFace
} from './ScoringControlAppearance'
import {
  OutTypeHeader,
  SCORING_MENU,
  ScoringMenuLabel
} from './ScoringMenuAppearance'
import { scoringMenuPanel, scoringMenuRow } from './scoringMenuWebStyle'

export { PRIMARY_OUTCOMES }
export function OutcomePicker({
  disabled,
  contactKind = 'swing',
  hasRunners = true,
  onChoose,
  onActive
}: OutcomePickerProps): React.ReactElement {
  const theme = useTheme()
  const label = contactKind === 'bunt' ? 'Bunt in Play' : 'Swing in Play'
  const available = (value: string): boolean =>
    (contactKind !== 'bunt' || value !== 'Home Run') &&
    outcomeAvailable(value, hasRunners)
  const outcomes = PRIMARY_OUTCOMES.filter(value => available(value))
  const trigger = useRef<HTMLButtonElement>(null)
  const [mode, setMode] = useState<'tap' | 'drag'>()
  const [submenu, setSubmenu] = useState(false)
  const [hover, setHover] = useState('')
  const [submenuTop, setSubmenuTop] = useState(200)
  const gesture = useRef<{ pointer: number; y: number; drag: boolean } | null>(
    null
  )
  const suppressClick = useRef(false)
  const previousX = useRef(0)
  const main = useRef<HTMLDivElement>(null)
  const outPanel = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    if (!submenu) return
    const measure = (): void => {
      const height =
        outPanel.current?.getBoundingClientRect().height ??
        SCORING_MENU.outPanelHeight
      setSubmenuTop(top =>
        Math.max(
          12,
          Math.min(top, window.innerHeight - SCORING_MENU.bottom - height)
        )
      )
    }
    measure()
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('resize', measure)
    }
  }, [submenu, submenuTop])
  const close = (): void => {
    setMode(undefined)
    setSubmenu(false)
    setHover('')
    onActive(false)
    trigger.current?.focus()
  }
  useEffect(() => {
    if (disabled) {
      const pointer = gesture.current?.pointer
      if (
        pointer != null &&
        trigger.current?.hasPointerCapture(pointer) === true
      )
        trigger.current.releasePointerCapture(pointer)
      gesture.current = null
      setMode(undefined)
      setSubmenu(false)
      setHover('')
      onActive(false)
    }
  }, [disabled, onActive])
  const select = (value: string): void => {
    if (disabled || !available(value)) return
    if (value === 'Batter Out') {
      setSubmenu(previous => !previous)
      return
    }
    close()
    onChoose(value as Outcome)
  }
  const hoverAt = (x: number, y: number): HTMLElement | undefined => {
    return (
      document
        .elementsFromPoint(x, y)
        .map(node => node.closest<HTMLElement>('[data-outcome]'))
        .find(node => node != null) ?? undefined
    )
  }
  const move = (event: React.PointerEvent): void => {
    const active = gesture.current
    if (disabled || active == null) return
    if (!active.drag && event.clientY < active.y - 8) {
      active.drag = true
      setMode('drag')
      onActive(true)
    }
    if (!active.drag) return
    const movingRight = event.clientX > previousX.current + 1
    previousX.current = event.clientX
    const item = hoverAt(event.clientX, event.clientY)
    const value = item?.dataset.outcome ?? ''
    setHover(value)
    if (value === 'Batter Out' && item != null) {
      setSubmenu(true)
      setSubmenuTop(
        Math.max(
          12,
          Math.min(
            window.innerHeight -
              SCORING_MENU.bottom -
              SCORING_MENU.outPanelHeight,
            item.getBoundingClientRect().top
          )
        )
      )
    } else if (
      (item != null && main.current?.contains(item) === true) ||
      (movingRight && event.clientX >= window.innerWidth / 2)
    )
      setSubmenu(false)
  }
  const release = (
    event: React.PointerEvent<HTMLDivElement>,
    cancel: boolean
  ): void => {
    const active = gesture.current
    gesture.current = null
    if (!disabled && active?.drag === true) {
      suppressClick.current = true
      const item = cancel ? undefined : hoverAt(event.clientX, event.clientY)
      const value = item?.dataset.outcome
      close()
      if (value != null && value !== 'Batter Out' && available(value))
        onChoose(value as Outcome)
    }
    const capture = event.target as HTMLElement
    if (capture.hasPointerCapture(event.pointerId))
      capture.releasePointerCapture(event.pointerId)
  }
  const buttonStyle: React.CSSProperties = {
    minHeight: 44,
    border: 0,
    borderRadius: theme.rem(theme.radiusRem),
    padding: '9px 12px',
    color: theme.primaryTextColor,
    background: theme.backgroundColor,
    fontFamily: theme.semiBoldTextFont,
    fontWeight: 700,
    fontSize: 16.8,
    cursor: 'pointer',
    touchAction: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none'
  }
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none'
      }}
      onContextMenu={event => {
        event.preventDefault()
      }}
      onPointerMove={move}
      onPointerUp={event => {
        release(event, false)
      }}
      onPointerCancel={event => {
        release(event, true)
      }}
      onLostPointerCapture={() => {
        if (gesture.current?.drag === true) close()
        gesture.current = null
      }}
      onKeyDown={event => {
        if (event.key === 'Escape') close()
      }}
    >
      <button
        ref={trigger}
        aria-label={label}
        title="Tap for outcomes or drag up to choose"
        disabled={disabled}
        style={{
          ...buttonStyle,
          background: CONTROL_COLORS.contact.bottom,
          border: `1px solid ${CONTROL_COLORS.contact.border}`,
          borderRadius: 12,
          boxShadow: '0 4px 3px rgba(0, 0, 0, 0.4)',
          color: '#ffffff',
          position: 'relative',
          padding: '0 3px',
          width: '100%',
          height: CONTROL_HEIGHT,
          opacity: disabled ? 0.5 : 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: theme.regularTextFont,
          fontSize: 16,
          fontWeight: 400
        }}
        onPointerDown={event => {
          if (disabled || event.button !== 0) return
          previousX.current = event.clientX
          suppressClick.current = false
          gesture.current = {
            pointer: event.pointerId,
            y: event.clientY,
            drag: false
          }
          event.currentTarget.setPointerCapture(event.pointerId)
        }}
        onClick={() => {
          if (disabled) return
          if (suppressClick.current) {
            suppressClick.current = false
            return
          }
          if (mode != null) close()
          else {
            setMode('tap')
            onActive(true)
          }
        }}
      >
        <ControlBackdrop kind="contact" />
        <ControlFace kind="contact" contact={contactKind} />
      </button>
      {mode != null && (
        <>
          <div
            aria-label="Dismiss outcomes"
            onClick={close}
            style={{ position: 'fixed', inset: 0, zIndex: 19 }}
          />
          <div
            ref={main}
            role="menu"
            aria-label={`${label} outcomes`}
            style={{
              ...scoringMenuPanel,
              position: 'fixed',
              zIndex: 20,
              right: 12,
              bottom: SCORING_MENU.bottom,
              width: 'min(230px, 46vw)',
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              maxHeight: `calc(100dvh - ${SCORING_MENU.bottom + 12}px)`,
              overflowY: 'auto'
            }}
          >
            {(mode === 'drag' ? [...outcomes].reverse() : outcomes).map(
              value => (
                <button
                  role="menuitem"
                  data-outcome={value}
                  key={value}
                  aria-label={value}
                  onPointerEnter={() => {
                    setHover(value)
                  }}
                  onPointerLeave={() => {
                    setHover('')
                  }}
                  onFocus={() => {
                    setHover(value)
                  }}
                  onBlur={() => {
                    setHover('')
                  }}
                  style={{
                    ...scoringMenuRow(hover === value)
                  }}
                  onClick={event => {
                    if (value === 'Batter Out')
                      setSubmenuTop(
                        Math.max(
                          12,
                          Math.min(
                            window.innerHeight -
                              SCORING_MENU.bottom -
                              SCORING_MENU.outPanelHeight,
                            event.currentTarget.getBoundingClientRect().top
                          )
                        )
                      )
                    select(value)
                  }}
                >
                  <ScoringMenuLabel
                    label={value}
                    active={hover === value}
                    chevron={value === 'Batter Out' ? 'left' : undefined}
                  />
                </button>
              )
            )}
          </div>
          {submenu && (
            <div
              role="menu"
              aria-label="Batter Out options"
              ref={outPanel}
              style={{
                ...scoringMenuPanel,
                position: 'fixed',
                zIndex: 20,
                left: 12,
                top: submenuTop,
                maxHeight: `calc(100dvh - ${SCORING_MENU.bottom + 12}px)`,
                overflowY: 'auto',
                width: 'min(230px, 46vw)',
                display: 'flex',
                flexDirection: 'column',
                gap: 0
              }}
            >
              <OutTypeHeader />
              {OUT_TYPES.map(value => (
                <button
                  role="menuitem"
                  key={value}
                  data-outcome={value}
                  aria-label={value}
                  onPointerEnter={() => {
                    setHover(value)
                  }}
                  onPointerLeave={() => {
                    setHover('')
                  }}
                  onFocus={() => {
                    setHover(value)
                  }}
                  onBlur={() => {
                    setHover('')
                  }}
                  style={{
                    ...scoringMenuRow(hover === value)
                  }}
                  onClick={() => {
                    if (disabled) return
                    select(value)
                  }}
                >
                  <ScoringMenuLabel label={value} active={hover === value} />
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
